import { Box, Divider, Typography } from '@mui/material';
import { compact } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikCheckbox,
  FormikNumericField,
  FormikTextField,
  HelpTooltip,
  PaymentTypeAmountLimits,
  TradeMethodTreeSelect,
} from 'components';
import { P2PProviderType } from 'enums';
import { useCurrencies, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { TradeMethod } from 'types';

import { ConfigOverridesField } from '../ConfigOverridesField';
import { P2PProviderCredentials } from '../P2PProviderCredentials';

type Props = {
  providerType?: P2PProviderType;
  fiatCurrencyId?: string;
  assetCurrencyId?: string;
  onTradeMethodsChange: (event: any) => void;
  isNew?: boolean;
};

export const P2PProviderPayinConfig: React.FC<Props> = ({
  providerType,
  assetCurrencyId,
  fiatCurrencyId,
  isNew,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const { getFiatCurrencyCode } = useCurrencies();

  const { tradeMethods, allTradeMethods } = useUserContext();

  const availableTradeMethods: TradeMethod[] = useMemo(
    () => (isNew ? tradeMethods : allTradeMethods),
    [isNew, tradeMethods, allTradeMethods],
  );

  const fiatCurrenciesIds = useMemo(
    () => compact([fiatCurrencyId]),
    [fiatCurrencyId],
  );

  return (
    <Fragment>
      <Typography variant="h5">{tCommon('common.payin')}</Typography>
      <FormikCheckbox label={t('fields.enabled')} name="payinConfig.enabled" />
      <FormikCheckbox
        label={t('fields.polling_enabled')}
        name="payinConfig.pollingEnabled"
      />
      <Box display="flex" alignItems="center">
        <FormikCheckbox
          label={t('fields.cancel_enabled')}
          name="payinConfig.cancelEnabled"
        />
        <HelpTooltip title={t('fields.cancel_enabled_description')} />
      </Box>
      <FormikTextField
        label={t('fields.url')}
        name="payinConfig.url"
        required
      />

      <P2PProviderCredentials
        path="payinConfig.credentials"
        providerType={providerType}
      />

      <FormikNumericField
        label={t('fields.response_timeout')}
        name="payinConfig.responseTimeout"
        suffix={tCommon('suffixes.ms')}
        allowNegative={false}
        fullWidth
        defaultValue={0}
        helperText={t('fields.response_timeout_description')}
      />
      <FormikNumericField
        label={t('fields.expected_fee')}
        name="payinConfig.expectedFee"
        allowNegative={false}
        percentageSuffix
        fullWidth
        required
      />

      <Fragment>
        <Typography>
          {`${t('fields.trade_methods')} (${tCommon('common.payin')})`}
        </Typography>

        <TradeMethodTreeSelect
          fieldName={'payinConfig.tradeMethods'}
          tradeMethods={availableTradeMethods}
          fiatCurrenciesIds={fiatCurrenciesIds}
        />
      </Fragment>

      <Box sx={{ mt: 2, mb: 3 }}>
        <PaymentTypeAmountLimits
          configPath="payinConfig"
          fiatCurrencyCode={getFiatCurrencyCode(fiatCurrencyId)}
        />
      </Box>

      <ConfigOverridesField
        fieldPath="payinConfig"
        providerType={providerType}
      />

      <Divider sx={{ mt: 2, mb: 2 }}>{t('fields.order_amount_limits')}</Divider>
      <FormControls row>
        <FormikNumericField
          name="payinConfig.orderMinAmount"
          label={tCommon('common.min')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
        <FormikNumericField
          name="payinConfig.orderMaxAmount"
          label={tCommon('common.max')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
      </FormControls>

      <FormikNumericField
        allowNegative={false}
        decimalScale={0}
        fullWidth
        label={t('fields.active_dispute_count')}
        name="payinConfig.activeDisputeCount"
      />

      <FormikCheckbox
        label={t('fields.allow_customer_bank')}
        name="payinConfig.allowCustomerBank"
      />

      <FormikCheckbox
        label={t('fields.allow_provider_amount_randomization')}
        name="payinConfig.allowProviderAmountRandomization"
      />
    </Fragment>
  );
};
